// ============================================================================
// CaseReportFormScreen.tsx — Tabbed case report form screen (parent shell)
// Handles initialization, save orchestration, auto-save, lock/unlock,
// tab system, and the CaseReportController for external callers.
// Section content delegated to sub-components in casereport/sections/.
// ============================================================================
import {
  useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';

// Service imports
import {
  CaseRpt,
  convertOfficersToFormFormat,
  convertFormOfficersToReport,
  convertAgenciesToFormFormat,
  convertFormAgenciesToReport,
  validateReport,
  canModifyReport,
  prepareReportForSave,
  getNarrativeContentForSave,
  buildReportFromCallCard,
  setupInvolved,
} from '@/services/case-report-service';
import { renderReactComponent } from '@/react-bridge';

// Sub-component imports (Incident tab — kept inline since no modals)
import { CaseReportIncidentDetails } from '@/components/casereport/CaseReportIncidentDetails';
import { CaseReportLocation } from '@/components/casereport/CaseReportLocation';
import { CaseReportOfficersAssigned } from '@/components/casereport/CaseReportOfficersAssigned';
import { CaseReportRespondingAgencies } from '@/components/casereport/CaseReportRespondingAgencies';

// Section components (own their modal state)
import { CaseReportInvolvedSection, type CaseReportInvolvedSectionHandle } from '@/components/casereport/sections/CaseReportInvolvedSection';
import { CaseReportOffenseSection } from '@/components/casereport/sections/CaseReportOffenseSection';
import { CaseReportAccidentSection } from '@/components/casereport/sections/CaseReportAccidentSection';
import { CaseReportPropertySection } from '@/components/casereport/sections/CaseReportPropertySection';
import { CaseReportMedicalSection } from '@/components/casereport/sections/CaseReportMedicalSection';
import { CaseReportEvidenceSection } from '@/components/casereport/sections/CaseReportEvidenceSection';
import { CaseReportReviewSection } from '@/components/casereport/sections/CaseReportReviewSection';

// Shared component imports
import { FormTabPanel } from '@/components/forms/FormTabPanel';
import { useFormSchemas } from '@/hooks/useFormSchemas';
import { RichTextEditor, RichTextEditorHandle } from '@/components/RichTextEditor';
import { navigateToMapByAddress, navigateToCaseReport, refreshPage } from '@/services/navigation';

// External globals
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { Codes } from '@/constants/codes';
import moment from 'moment-timezone';
import { hasValue, hasStringValue, hasNumericValue, currentUserHasPermission, getOfficerLabel } from '@/utils/legacy-utils';
import { addCompanyNameField, listHasOtherCompanyItems } from '@/utils/call-card-helpers';
import { AccessAuthorities } from '@/models';
import { getOfficerPluralLabel, getCurrentUserId, getCurrentCompanyId } from '@/utils/auth';
import { isInArrayByProperty } from '@/utils/arrays';
import { validateAddressWithSuggestions } from '@/utils/custom-location';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const hasReportApproval = _wg('hasReportApproval');
const getFormattedDisposition = _wg('getFormattedDisposition');
const goToCaseReportsScreen = _wg('goToCaseReportsScreen');
const exitCaseReportScreenWithoutDirtyChecking = _wg('exitCaseReportScreenWithoutDirtyChecking');
const mergeCurrentNatureOfCallIntoNatureOfCallList = _wg('mergeCurrentNatureOfCallIntoNatureOfCallList');
const openPage = _wg('openPage');
const fillInAddressFromSuggestions = _wg('fillInAddressFromSuggestions');
const getCustomLocationListPromise = _wg('getCustomLocationListPromise');

// ── Helpers ─────────────────────────────────────────────────────────────

const narrativeToolbarConfig = App.isMobile() ? 'mobile' : [
  'formatting', 'fontName', 'fontSize',
  'bold', 'italic', 'underline', 'strikethrough',
  'backColor', 'foreColor', 'separator',
  'insertOrderedList', 'insertUnorderedList', 'indent', 'outdent',
  'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull',
  'lineHeight', 'separator',
  'createLink', 'unlink', 'insertImage', 'uploadImage', 'separator',
  'insertUpperRomanList', 'insertLowerRomanList',
  'createTable',
  'addRowAbove', 'addRowBelow', 'addColumnLeft', 'addColumnRight',
  'deleteRow', 'deleteColumn',
  'mergeCellsHorizontally', 'mergeCellsVertically',
  'splitCellHorizontally', 'splitCellVertically',
  'tableAlignLeft', 'tableAlignCenter', 'tableAlignRight', 'separator',
  'undo', 'redo', 'cleanFormatting', 'copyFormat', 'applyFormat', 'separator',
  'print', 'exportPdf',
];

const ALL_TAB_IDS = [
  'Incident', 'Involved', 'Agencies', 'Offense', 'Accident',
  'Property', 'Medical', 'Evidence', 'Narrative', 'ReviewSubmit',
] as const;

// ── Props ───────────────────────────────────────────────────────────────

interface CaseReportFormScreenProps {
  reportId?: string;
  isLog?: boolean;
  viewOnly?: boolean;
  currentTab?: string;
}

// ── Component ───────────────────────────────────────────────────────────

export function CaseReportFormScreen({ reportId, isLog: isLogProp = true, viewOnly = false, currentTab: initialTab }: CaseReportFormScreenProps) {
  const { theme } = useTheme();

  // ── Initialization state ──────────────────────────────────────────

  const [isInitialized, setIsInitialized] = useState(false);
  const [report, setReport] = useState<any>(null);

  const officerLabel = useMemo(() => getOfficerLabel(), []);
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);

  // ── Reactive list state (triggers re-renders on CRUD) ──────────────

  const [officers, setOfficers] = useState<any[]>([]);
  const [agencies, setAgencies] = useState<any[]>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [propertyList, setPropertyList] = useState<any[]>([]);
  const [offenders, setOffenders] = useState<any[]>([]);
  const [medicalReports, setMedicalReports] = useState<any[]>([]);
  const [statementList, setStatementList] = useState<any[]>([]);
  const [digitalEvidenceList, setDigitalEvidenceList] = useState<any[]>([]);
  const [accident, setAccident] = useState<any>({ accidentPersonList: [], roadwayConditions: '', findings: '', notes: '' });
  const [availableOfficers, setAvailableOfficers] = useState<any[]>([]);

  // ── Narrative state ────────────────────────────────────────────────

  const [narrativeContent, setNarrativeContent] = useState('');
  const narrativeContentRef = useRef('');
  const narrativeEditorRef = useRef<RichTextEditorHandle>(null);

  // Sync narrativeContent ref with state
  useEffect(() => { narrativeContentRef.current = narrativeContent; }, [narrativeContent]);

  // ── Save / read-only state ────────────────────────────────────────

  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const isReadOnly = useMemo(() => report?.isApproved === true, [report?.isApproved]);

  // ── Auto-save / lock refs ─────────────────────────────────────────

  const autoSaveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lockedReportIdRef = useRef<string | null>(null);

  // ── Section refs (for Vue shim) ────────────────────────────────────

  const involvedSectionRef = useRef<CaseReportInvolvedSectionHandle>(null);

  // ── Tab system state ──────────────────────────────────────────────

  const [activeTab, setActiveTab] = useState(initialTab || 'Incident');
  const activeTabRef = useRef(activeTab);
  const [visibleTabIds, setVisibleTabIds] = useState<string[]>([]);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabsListRef = useRef<HTMLUListElement>(null);
  const tabsMoreRef = useRef<HTMLDivElement>(null);
  const tabWidthCacheRef = useRef<Map<string, number>>(new Map());
  const isCalculatingTabsRef = useRef(false);

  // ── Dropdown option state ─────────────────────────────────────────

  const [natureOfCallOptions, setNatureOfCallOptions] = useState<any[]>([]);
  const [offenseOptions, setOffenseOptions] = useState<any[]>([]);
  const [evidenceOfOptions, setEvidenceOfOptions] = useState<any[]>([]);
  const [reportedAsOptions, setReportedAsOptions] = useState<any[]>([]);

  // ── Computed values ───────────────────────────────────────────────

  const involvedPersonOptions = useMemo(() =>
    people.map((p: any) => ({ id: p.id, name: p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown' })),
    [people]);

  const involvedVehicleOptions = useMemo(() =>
    vehicles.map((v: any) => ({ id: v.id, name: `${v.year || ''} ${v.make || ''} ${v.model || ''}`.trim() || 'Unknown' })),
    [vehicles]);

  const propertyOwnerOptions = useMemo(() => [
    ...people.map((p: any) => ({ id: p.id + '_P', name: p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown' })),
    ...businesses.map((b: any) => ({ id: b.id + '_B', name: b.businessName || b.name || 'Unknown Business' })),
    ...clients.map((c: any) => ({ id: c.id + '_C', name: c.name || 'Unknown Client' })),
  ], [people, businesses, clients]);

  const allInvolvedOptions = useMemo(() => [
    ...people.map((p: any) => ({ id: p.id + '_P', name: (p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown') + ' (Person)' })),
    ...vehicles.map((v: any) => ({ id: v.id + '_V', name: (`${v.year || ''} ${v.make || ''} ${v.model || ''}`.trim() || 'Unknown') + ' (Vehicle)' })),
    ...businesses.map((b: any) => ({ id: b.id + '_B', name: (b.businessName || b.name || 'Unknown Business') + ' (Business)' })),
    ...clients.map((c: any) => ({ id: c.id + '_C', name: (c.name || 'Unknown Client') + ' (Client)' })),
  ], [people, vehicles, businesses, clients]);

  const canSaveNpsdData = useMemo(() => currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_SAVE), []);
  const canUnapprove = useMemo(() => currentUserHasPermission(AccessAuthorities.ROLE_UNAPPROVE_CASE_REPORTS), []);
  const hasApprovalPerm = useMemo(() => hasReportApproval(), []);
  const isAuthor = useMemo(() => {
    const uid = getCurrentUserId()?.toString();
    return report?.authorId && uid === report.authorId.toString();
  }, [report?.authorId]);

  const dispatcherNotes = useMemo(() =>
    report ? getFormattedDisposition(report.callDispositionValue, report.dispositionNotes) : '',
    [report?.callDispositionValue, report?.dispositionNotes]);

  // ── Custom Forms ──────────────────────────────────────────────────
  // One dynamic tab per CASE_REPORT-attached schema (decision row 7 — one
  // instance per schema + host). 'display' scope: module-scoped read endpoint,
  // so anyone who can open the report sees its form tabs. The schema list does
  // not depend on the report, so it loads as soon as the screen is ready — a
  // report that hasn't been saved yet shows the tabs with a "save first" panel.

  const { schemas: formSchemas } = useFormSchemas({
    attachTo: 'CASE_REPORT',
    autoLoad: isInitialized,
    scope: 'display',
  });

  // Form tabs have their own write permission (ROLE_FORM_ENTRY — one grant
  // covering save/update/delete) and otherwise follow the report body's own
  // edit rule (canModifyReport: not approved, and not submitted unless the
  // user holds report approval). The backend enforces the same rule.
  const canEditForms = useMemo(() => currentUserHasPermission(AccessAuthorities.ROLE_FORM_ENTRY), []);
  const formsEditable = useMemo(
    () => canEditForms && canModifyReport(report),
    [canEditForms, report?.isApproved, report?.isSubmitted], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // The review handlers are identity-stable useCallback([]) closures over the
  // first render's handleSaveReport, so the save path reads these through refs.
  const formSchemasRef = useRef(formSchemas);
  formSchemasRef.current = formSchemas;
  const formsEditableRef = useRef(formsEditable);
  formsEditableRef.current = formsEditable;

  const formTabIds = useMemo(() => (formSchemas || []).map((s) => `Form_${s.id}`), [formSchemas]);

  const formTabLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const s of formSchemas || []) labels[`Form_${s.id}`] = s.name;
    return labels;
  }, [formSchemas]);

  // Custom form tabs belong with the report's own content tabs, so they slot in
  // ahead of Submit / Audit, which stays the last tab.
  const allTabIds = useMemo<string[]>(() => [
    ...ALL_TAB_IDS.filter((id) => id !== 'ReviewSubmit'),
    ...formTabIds,
    'ReviewSubmit',
  ], [formTabIds]);

  // calculateVisibleTabs is deliberately identity-stable (it is a resize-listener
  // dependency), so it reads the current tab ids through a ref rather than a
  // closure. Assigned during render so the layout effect below sees fresh ids.
  const allTabIdsRef = useRef<string[]>(allTabIds);
  allTabIdsRef.current = allTabIds;

  // Form-tab saves piggyback on the report save. We bump this counter as the
  // report save fires; each FormTabPanel watches it and writes its dirty values
  // to its own instance endpoint in parallel.
  const [formSaveTrigger, setFormSaveTrigger] = useState(0);

  // Each form panel reports its required-field validation errors here so the
  // report save can pre-validate everything before firing. Map<schemaId, errors[]>.
  const formValidationsRef = useRef<Map<number, string[]>>(new Map());
  const handleFormValidation = useCallback((sid: number, errors: string[]) => {
    if (errors.length > 0) {
      formValidationsRef.current.set(sid, errors);
    } else {
      formValidationsRef.current.delete(sid);
    }
  }, []);

  // Schema ids of form panels with unsaved changes.
  const dirtyFormIdsRef = useRef<Set<number>>(new Set());
  const handleFormDirtyChange = useCallback((sid: number, dirty: boolean) => {
    if (dirty) {
      dirtyFormIdsRef.current.add(sid);
    } else {
      dirtyFormIdsRef.current.delete(sid);
    }
  }, []);

  // Tab counts
  const involvedCount = useMemo(() => people.length + vehicles.length + businesses.length + clients.length, [people, vehicles, businesses, clients]);
  const agenciesCount = useMemo(() => report?.respondingAgencies?.length || 0, [report?.respondingAgencies]);
  const offenseCount = useMemo(() => offenders.length, [offenders]);
  const accidentCount = useMemo(() => accident.accidentPersonList?.length || 0, [accident.accidentPersonList]);
  const propertyCount = useMemo(() => propertyList.length, [propertyList]);
  const medicalCount = useMemo(() => medicalReports.length, [medicalReports]);
  const evidenceCount = useMemo(() => digitalEvidenceList.length + statementList.length, [digitalEvidenceList, statementList]);

  const reviewTabLabel = useMemo(() => {
    if (report?.isApproved) return 'Audit Log';
    if (report?.isSubmitted) return 'Approve / Return';
    return 'Submit / Audit';
  }, [report?.isApproved, report?.isSubmitted]);

  const overflowTabIds = useMemo(() => allTabIds.filter(id => !visibleTabIds.includes(id)), [allTabIds, visibleTabIds]);

  const saveButtonText = useMemo(() => isSaving ? 'Saving...' : 'Save', [isSaving]);

  // ── Effects ───────────────────────────────────────────────────────

  // Calculate visible tabs on mount + resize
  const calculateVisibleTabs = useCallback(() => {
    if (isCalculatingTabsRef.current) return;
    isCalculatingTabsRef.current = true;

    const container = tabsContainerRef.current;
    const tabsList = tabsListRef.current;
    if (!container || !tabsList) {
      isCalculatingTabsRef.current = false;
      return;
    }

    const tabElements = Array.from(tabsList.querySelectorAll('.case-report-tab')) as HTMLElement[];
    const gap = 4;
    const buffer = 5;
    const moreBtnWidth = 80;

    for (const tab of tabElements) {
      const tabId = tab.getAttribute('data-tab-id');
      if (!tabId) continue;
      const w = tab.offsetWidth;
      if (w > 0) {
        tabWidthCacheRef.current.set(tabId, w);
      }
    }

    const containerWidth = container.clientWidth;
    const tabIds = allTabIdsRef.current;

    let totalWidth = 0;
    let idx = 0;
    for (const tabId of tabIds) {
      const w = tabWidthCacheRef.current.get(tabId) || 100;
      totalWidth += w + (idx > 0 ? gap : 0);
      idx++;
    }

    if (totalWidth <= containerWidth + buffer) {
      setVisibleTabIds([...tabIds]);
      isCalculatingTabsRef.current = false;
      return;
    }

    const availableWidth = containerWidth - moreBtnWidth;
    totalWidth = 0;
    const newVisible: string[] = [];

    for (const tabId of tabIds) {
      const w = tabWidthCacheRef.current.get(tabId) || 100;
      const tabWidth = w + (newVisible.length > 0 ? gap : 0);
      if (totalWidth + tabWidth <= availableWidth) {
        totalWidth += tabWidth;
        newVisible.push(tabId);
      } else {
        break;
      }
    }

    setVisibleTabIds(newVisible);
    isCalculatingTabsRef.current = false;
  }, []);

  useEffect(() => {
    tabWidthCacheRef.current.clear();

    let rafId: number | null = null;
    const throttledResize = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        calculateVisibleTabs();
      });
    };
    window.addEventListener('resize', throttledResize);
    return () => {
      window.removeEventListener('resize', throttledResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [calculateVisibleTabs]);

  // Recalculate tabs when report loads (refs become available after loading state)
  // useLayoutEffect runs after DOM update but before paint, preventing flash
  useLayoutEffect(() => {
    if (!isInitialized || !report) return;
    tabWidthCacheRef.current.clear();
    calculateVisibleTabs();
  }, [isInitialized, report, allTabIds, calculateVisibleTabs]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tabsMoreRef.current && !tabsMoreRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    api.listNatureOfCall((result: any) => {
      const data = mergeCurrentNatureOfCallIntoNatureOfCallList(
        CaseRpt.currentMDTRpt.natureOfCall,
        CaseRpt.currentMDTRpt.natureOfCallValues,
        result.natureOfCallList
      );
      // Merged-in items (NOCs on the report but no longer in the list) need company names too.
      addCompanyNameField(data, result.companyList);
      setNatureOfCallOptions(data);
    }, () => {
      App.showPopupNotification('Error getting nature of call data.', 'error');
    });
  }, [isInitialized]);

  useEffect(() => {
    setReportedAsOptions(Codes.getPropertyCodes?.() || []);
    setOffenseOptions(Codes.getOffenseCodes?.() || []);
    setEvidenceOfOptions(Codes.getDigitalEvidenceCodes?.() || []);
  }, []);

  useEffect(() => {
    if (!isInitialized || !report) return;
    const content = narrativeContent;
    const rptId = report.id;
    if (hasValue(content) && content.indexOf('data-attachment-id') !== -1 && rptId) {
      api.listCaseReportNarrativeAttachments(parseInt(rptId), (result: any) => {
        const list = result.attachmentList;
        if (!list || list.length === 0) return;
        const map: Record<string, string> = {};
        list.forEach((a: any) => { map[String(a.id)] = a.signedURL; });
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        tempDiv.querySelectorAll('img[data-attachment-id]').forEach((img) => {
          const aid = String(img.getAttribute('data-attachment-id'));
          if (map[aid]) img.setAttribute('src', map[aid]);
        });
        setNarrativeContent(tempDiv.innerHTML);
      }, () => {});
    }
  }, [isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Initialization effect ─────────────────────────────────────────

  useEffect(() => {
    App.npsdSearchType = 'addRpt';
    CaseRpt.vehicleOwners = { id: '', Connections: { People: [], Businesses: [], Clients: [] } };
    CaseRpt.vehicles = [];
    getCustomLocationListPromise();

    const populateState = (rpt: any, resultForInvolved: any) => {
      const narrativeStr = rpt.narrative || '';
      delete rpt.narrative;

      setReport(rpt);
      setOfficers(convertOfficersToFormFormat(rpt));
      setAgencies(convertAgenciesToFormFormat(rpt.respondingAgencies));
      setPeople(rpt.npsdConnectionsNew?.people?.slice() || []);
      setVehicles(rpt.npsdConnectionsNew?.vehicles?.slice() || []);
      setBusinesses(rpt.npsdConnectionsNew?.businesses?.slice() || []);
      setClients(rpt.npsdConnectionsNew?.clients?.slice() || []);
      setPropertyList(rpt.propertyList?.slice() || []);
      setOffenders(rpt.offenders?.slice() || []);
      setMedicalReports(rpt.medicalReports?.slice() || []);
      setStatementList(rpt.statementList?.slice() || []);
      setDigitalEvidenceList(rpt.digitalEvidenceList?.slice() || []);
      setAccident(rpt.accident || { accidentPersonList: [], roadwayConditions: '', findings: '', notes: '' });
      setNarrativeContent(narrativeStr);

      setupInvolved(resultForInvolved, CaseRpt.vehicles);
      setIsInitialized(true);

      api.getAvailableMDTOfficers((officerResult: any) => {
        const officerList = officerResult.officerList.map((o: any) => ({
          id: o.id,
          name: `${o.firstName} ${o.lastName}`,
        }));

        setAvailableOfficers(officerList);

        setOfficers(prev => prev.map(officer => {
          const found = officerList.find((o: any) => o.id === officer.officerId);
          return {
            ...officer,
            name: found ? found.name : `Select ${getOfficerLabel()}`,
          };
        }));
      });
    };

    if (!reportId) {
      CaseRpt.newRpt = true;
      const rpt = new (window as any).mdtReport('');
      CaseRpt.currentMDTRpt = rpt;
      App.originalReportResult = new (window as any).mdtReport('');

      let callCardData: any = null;
      const callCardDataStr = localStorage.getItem('ReportFromCallCard');
      if (callCardDataStr != null) {
        callCardData = JSON.parse(callCardDataStr);
        if (callCardData != null) {
          buildReportFromCallCard(rpt, callCardData);
          localStorage.removeItem('ReportFromCallCard');
          callCardData.id = null;

          rpt.authorTimeZoneId = moment.tz.guess();
          api.saveReport(rpt, (result: any) => {
            App.originalReportResult = null;
            navigateToCaseReport(result.id, false, true);
          }, (error: any) => {
            App.showPopupNotification('Report Failed to Save', 'error');
            if (hasValue(error) && hasValue(error.responseJSON) && hasValue(error.responseJSON.errors) &&
              hasValue(error.responseJSON.errors._embedded) && hasValue(error.responseJSON.errors._embedded.errors) &&
              isInArrayByProperty(error.responseJSON.errors._embedded.errors, 'errorCode', 'com.kerkton.orna.command.NPSDBusinessConnectionBaseCommand.ids.invalid')) {
              rpt.npsdConnectionsNew.people = [];
              rpt.npsdConnectionsNew.vehicles = [];
              App.showPopupNotification('People and vehicles removed due to data access restrictions.', 'warning');
            }
            populateState(rpt, callCardData);
          });
          return;
        }
      }
      populateState(rpt, callCardData);
    } else {
      CaseRpt.newRpt = false;

      let isLog = isLogProp;
      if (isLog === undefined || isLog === null) {
        isLog = true;
      } else if (typeof isLog === 'string') {
        isLog = (isLog as any).toLowerCase() === 'true';
      } else if (typeof isLog !== 'boolean') {
        isLog = true;
      }

      const reportReceived = (result: any) => {
        const rpt = new (window as any).mdtReport(result);
        CaseRpt.currentMDTRpt = rpt;

        populateState(rpt, result);

        App.originalReportResult = JSON.parse(JSON.stringify(rpt));
        if (rpt.isApproved) {
          App.originalReportResult = null;
        }
      };

      const shouldLock = viewOnly?.toString() !== 'true' && canModifyReport(CaseRpt.currentMDTRpt);
      if (shouldLock) {
        CaseRpt.lockedReportId = reportId;
        lockedReportIdRef.current = reportId;
        api.lockReport(reportId, true, () => {
          api.getReport(reportId, isLog, reportReceived);
        }, () => {
          api.getReport(reportId, isLog, reportReceived);
        });
      } else {
        api.getReport(reportId, isLog, reportReceived);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── CaseReportController for external callers ────────────────────

  useEffect(() => {
    if (!isInitialized) return;

    CaseRpt.currentMDTRptController = {
      isSearchOpen: () => involvedSectionRef.current?.isSearchOpen() ?? false,
      get activeTab() { return activeTabRef.current; },
      get isSaving() { return isSavingRef.current; },
      set isSaving(val: boolean) { setIsSaving(val); isSavingRef.current = val; },
      get narrativeContent() { return narrativeContentRef.current; },
      get npsdConnectionsNew() { return CaseRpt.currentMDTRpt?.npsdConnectionsNew; },
      get narrativeEditor() { return narrativeEditorRef.current; },
      openViewPersonModal: (id: number) => involvedSectionRef.current?.openViewPerson(id),
      openViewVehicleModal: (id: number) => involvedSectionRef.current?.openViewVehicle(id),
      openViewBusinessModal: (id: number) => involvedSectionRef.current?.openViewBusiness(id),
      openViewClientModal: (id: number) => involvedSectionRef.current?.openViewClient(id),
      closeInvolvedSearchModal: () => involvedSectionRef.current?.closeSearch(),
      refreshNpsd: () => refreshNpsdFromReport(),
      destroy: () => {
        const c = document.getElementById('CaseReportContainer');
        if (c) (window as any).OrnaReact.destroyComponent(c);
      },
    };

    return () => {
      CaseRpt.currentMDTRptController = null;
    };
  }, [isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-save effect ──────────────────────────────────────────────

  useEffect(() => {
    if (!isInitialized || isReadOnly || !report?.id || CaseRpt.newRpt) return;

    const id = setInterval(() => {
      if (isSavingRef.current) return;
      handleSaveReport(false, true);
    }, 60000);
    autoSaveIntervalRef.current = id;

    return () => {
      clearInterval(id);
      clearAutoSave();
    };
  }, [isInitialized, isReadOnly, report?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Lock cleanup on unmount ───────────────────────────────────────

  useEffect(() => {
    return () => {
      if (lockedReportIdRef.current) {
        api.lockReport(lockedReportIdRef.current, false, () => {}, () => {});
        CaseRpt.lockedReportId = null;
        lockedReportIdRef.current = null;
      }
    };
  }, []);

  // ── Save orchestration ────────────────────────────────────────────

  function clearAutoSave() {
    if (autoSaveIntervalRef.current !== null) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }
  }

  function resetAutoSave() {
    clearAutoSave();
    autoSaveIntervalRef.current = setInterval(() => {
      if (isSavingRef.current) return;
      handleSaveReport(false, true);
    }, 60000);
  }

  /**
   * Required-field errors reported by the custom form tabs, one message per
   * form. Empty when the panels are read-only — their state must not block a
   * report save the user is otherwise allowed to make.
   */
  function collectFormValidationMessages(): string[] {
    if (!formsEditableRef.current) return [];
    const messages: string[] = [];
    formValidationsRef.current.forEach((errs, sid) => {
      const schema = (formSchemasRef.current || []).find((s) => s.id === sid);
      messages.push(`${schema?.name ?? `Form ${sid}`}: ${errs.join(', ')}`);
    });
    return messages;
  }

  function handleSaveReport(exit: boolean, isAutoSave: boolean = false) {
    clearAutoSave();
    const errors = validateReport(CaseRpt.currentMDTRpt);
    if (errors.length > 0) {
      if (isAutoSave) {
        setIsSaving(false);
        isSavingRef.current = false;
        if (!CaseRpt.newRpt) resetAutoSave();
        return;
      }
      for (const error of errors) {
        App.showPopupNotification(error.message, error.type);
      }
      setIsSaving(false);
      isSavingRef.current = false;
      if (!CaseRpt.newRpt) resetAutoSave();
      return;
    }

    // Pre-validate the custom form tabs so a required field can't be quietly
    // dropped. Auto-save never blocks on form state — it skips the form write
    // and lets the report save proceed.
    const formValidationMessages = collectFormValidationMessages();
    if (formValidationMessages.length > 0 && !isAutoSave) {
      App.showPopupNotification(
        `Cannot save — fix required fields first. ${formValidationMessages.join('; ')}`,
        'error',
      );
      setIsSaving(false);
      isSavingRef.current = false;
      if (!CaseRpt.newRpt) resetAutoSave();
      return;
    }

    // Form writes are rejected once the report is approved, and the panel saves
    // run concurrently with the report request, so approving with unsaved form
    // edits could lose them. Hold the approval until the forms are saved.
    if (
      CaseRpt.currentMDTRpt.reportApproved === true &&
      formsEditableRef.current &&
      dirtyFormIdsRef.current.size > 0
    ) {
      if (!isAutoSave) {
        App.showPopupNotification('Save the report before approving so the form changes are kept.', 'error');
      }
      setIsSaving(false);
      isSavingRef.current = false;
      if (!CaseRpt.newRpt) resetAutoSave();
      return;
    }

    let narrativeData: { content: string; attachmentInfoList: any[] };
    try {
      narrativeData = getNarrativeContentForSave(narrativeEditorRef.current, narrativeContentRef.current);
      prepareReportForSave(CaseRpt.currentMDTRpt, narrativeData);
    } catch (e: any) {
      const errMsg = `prepareReportForSave error: ${e?.message || e}`;
      console.error(errMsg, e);
      api.insertNewJSError(errMsg, 'CaseReportFormScreen/handleSaveReport', e?.stack || null, 'caught', null);
      App.showPopupNotification('Error preparing report for save. Please try again.', 'error');
      setIsSaving(false);
      isSavingRef.current = false;
      if (!CaseRpt.newRpt) resetAutoSave();
      return;
    }

    CaseRpt.tempOriginalMDTReport = App.originalReportResult;
    App.originalReportResult = null;

    // Trigger any dirty form-tab panels to save in parallel with the report.
    // Bumped before the report request goes out so the panels' writes are in
    // flight even when a successful save navigates the screen away.
    if (formsEditableRef.current && formValidationMessages.length === 0) {
      setFormSaveTrigger((t) => t + 1);
    }

    if (CaseRpt.newRpt) {
      CaseRpt.currentMDTRpt.authorTimeZoneId = moment.tz.guess();
      api.saveReport(CaseRpt.currentMDTRpt, (result: any) => {
        CaseRpt.tempOriginalMDTReport = null;
        if (exit) {
          goToCaseReportsScreen();
        } else {
          const currentTab = activeTabRef.current || 'Incident';
          navigateToCaseReport(result.id, false, true, undefined, currentTab);
        }
      }, () => {
        App.originalReportResult = CaseRpt.tempOriginalMDTReport;
        CaseRpt.tempOriginalMDTReport = null;
        setIsSaving(false);
        isSavingRef.current = false;
        if (!CaseRpt.newRpt) resetAutoSave();
      });
    } else {
      const noLoadScreen = isAutoSave;
      if (isAutoSave) {
        setIsSaving(true);
        isSavingRef.current = true;
      }
      api.updateReport(CaseRpt.currentMDTRpt, noLoadScreen, () => {
        if (CaseRpt.currentMDTRpt.reportApproved !== true && CaseRpt.currentMDTRpt.isApproved !== true) {
          resetAutoSave();
        }
        setIsSaving(false);
        isSavingRef.current = false;
        if (!isAutoSave) {
          CaseRpt.tempOriginalMDTReport = null;
          const currentTab = activeTabRef.current || 'Incident';
          if (exit) {
            goToCaseReportsScreen();
          } else {
            navigateToCaseReport(CaseRpt.currentMDTRpt.id, false, false, undefined, currentTab);
          }
        }
      }, () => {
        App.originalReportResult = CaseRpt.tempOriginalMDTReport;
        CaseRpt.tempOriginalMDTReport = null;
        setIsSaving(false);
        isSavingRef.current = false;
        if (!CaseRpt.newRpt && CaseRpt.currentMDTRpt.reportApproved !== true && CaseRpt.currentMDTRpt.isApproved !== true) {
          resetAutoSave();
        }
      });
    }
  }

  function handleSaveWithAddressValidation(exit: boolean) {
    if (isSavingRef.current) return;
    clearAutoSave();
    setIsSaving(true);
    isSavingRef.current = true;
    const addressValidationErrorMsg = 'Error validating incident location. Please re-enter the incident location address/intersection.';

    const rpt = CaseRpt.currentMDTRpt;
    if (!rpt) { setIsSaving(false); isSavingRef.current = false; return; }

    // If address hasn't changed since load and we have cached coords, skip geocoding.
    const orig = App.originalReportResult;
    if (orig &&
        hasNumericValue(orig.latitude) && hasNumericValue(orig.longitude) &&
        orig.callLocFormattedAddress === rpt.callLocFormattedAddress) {
      handleSaveReport(exit, false);
      return;
    }

    validateAddressWithSuggestions({
      address: rpt.callLocFormattedAddress || '',
      errorMessage: addressValidationErrorMsg,
      isLocationRequired: false,
      showLoadingScreen: true,
      companyId: getCurrentCompanyId(),
      // Previously-saved coords for discrepancy detection on edits.
      previousLat: (orig as any)?.latitude ?? null,
      previousLng: (orig as any)?.longitude ?? null,
    })
      .then(locationResultsObj => {
        const locationField = document.getElementById('txtValidateableLocation') as HTMLInputElement | null;
        fillInAddressFromSuggestions(locationResultsObj, CaseRpt.currentMDTRpt, locationField, (window as any).DataObjectLocationFields.caseReport);
        handleSaveReport(exit, false);
      })
      .catch(() => {
        setIsSaving(false);
        isSavingRef.current = false;
        if (!CaseRpt.newRpt) resetAutoSave();
      });
  }

  // ── Tab handlers ──────────────────────────────────────────────────

  const selectTab = useCallback((tabName: string) => {
    setActiveTab(tabName);
    activeTabRef.current = tabName;
    setShowMoreMenu(false);
  }, []);

  const getTabLabel = useCallback((tabId: string) => {
    if (tabId === 'ReviewSubmit') return reviewTabLabel;
    return formTabLabels[tabId] ?? tabId;
  }, [reviewTabLabel, formTabLabels]);

  const getTabCount = useCallback((tabId: string): number | null => {
    const counts: Record<string, number> = {
      Involved: involvedCount, Agencies: agenciesCount, Offense: offenseCount,
      Accident: accidentCount, Property: propertyCount, Medical: medicalCount, Evidence: evidenceCount,
    };
    return counts[tabId] ?? null;
  }, [involvedCount, agenciesCount, offenseCount, accidentCount, propertyCount, medicalCount, evidenceCount]);

  // ── Sync helpers ──────────────────────────────────────────────────

  const refreshNpsdFromReport = useCallback(() => {
    const conn = CaseRpt.currentMDTRpt?.npsdConnectionsNew;
    if (!conn) return;
    setPeople(conn.people?.slice() || []);
    setVehicles(conn.vehicles?.slice() || []);
    setBusinesses(conn.businesses?.slice() || []);
    setClients(conn.clients?.slice() || []);
  }, []);

  // ── Incident tab handlers (kept inline — no modals) ───────────────

  const handleOfficersChange = useCallback((newOfficers: any[]) => {
    setOfficers(newOfficers);
    convertFormOfficersToReport(newOfficers, CaseRpt.currentMDTRpt);
  }, []);

  const handleAgenciesChange = useCallback((newAgencies: any[]) => {
    setAgencies(newAgencies);
    convertFormAgenciesToReport(newAgencies, CaseRpt.currentMDTRpt);
  }, []);

  const handleNatureChange = useCallback((value: any) => {
    CaseRpt.currentMDTRpt.natureOfCall = value;
    setReport((prev: any) => ({ ...prev, natureOfCall: value }));
  }, []);

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    if (fieldName && CaseRpt.currentMDTRpt) {
      CaseRpt.currentMDTRpt[fieldName] = value;
      setReport((prev: any) => ({ ...prev, [fieldName]: value }));
    }
  }, []);

  const handleLocationChange = useCallback((updatedReport: any) => {
    if (CaseRpt.currentMDTRpt) {
      CaseRpt.currentMDTRpt.callLocFormattedAddress = updatedReport.callLocFormattedAddress;
      CaseRpt.currentMDTRpt.callLocAddress2 = updatedReport.callLocAddress2;
      CaseRpt.currentMDTRpt.callLocName = updatedReport.callLocName;
    }
    setReport(updatedReport);
  }, []);

  const handleLocationBlur = useCallback(() => {
    if (hasValue(report.callLocFormattedAddress)) {
      CaseRpt.currentMDTRpt.callLocFormattedAddress = report.callLocFormattedAddress;
    }
  }, [report?.callLocFormattedAddress]);

  const handleViewMap = useCallback(() => {
    navigateToMapByAddress(report.callLocFormattedAddress || '');
  }, [report?.callLocFormattedAddress]);

  // ── Narrative handlers ────────────────────────────────────────────

  const handleNarrativeImageUpload = useCallback((insertCallback: any) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png,image/gif,image/bmp,image/webp';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);
    fileInput.onchange = () => {
      if (!fileInput.files?.length) { document.body.removeChild(fileInput); return; }
      App.uploadFromFileInput(fileInput, {
        updateAttachmentInfo: (_idx: number, info: any) => { (window as any).editorAttachmentKeys[info.id] = info.activationKey; },
        successCallback: (_idx: number, att: any) => {
          insertCallback(att.signedURL, att.title, null, null, att.id);
        },
      }, (window as any).ATTACHMENT_TYPES.CASE_REPORT_NARRATIVE_EDITOR);
      document.body.removeChild(fileInput);
    };
    fileInput.click();
  }, []);

  // ── Review action handlers ────────────────────────────────────────

  const handleSave = useCallback(() => {
    CaseRpt.currentMDTRpt.reportApproved = false;
    handleSaveWithAddressValidation(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitForApproval = useCallback(() => {
    CaseRpt.currentMDTRpt.reportSubmittedForApproval = true;
    CaseRpt.currentMDTRpt.reportApproved = false;
    handleSaveWithAddressValidation(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = useCallback(() => {
    CaseRpt.currentMDTRpt.reportApproved = true;
    handleSaveWithAddressValidation(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnapprove = useCallback(() => {
    api.unapproveCaseReport(report.id, () => {
      refreshPage();
    }, () => {
      App.showPopupNotification('Error unapproving report', 'error');
    });
  }, [report?.id]);

  const handleReturnToAuthor = useCallback((comment: string) => {
    CaseRpt.currentMDTRpt.reportApproved = false;
    CaseRpt.currentMDTRpt.returnToAuthorComments = comment;
    handleSaveWithAddressValidation(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExit = useCallback(() => goToCaseReportsScreen(), []);
  const handleExitWithoutSave = useCallback(() => exitCaseReportScreenWithoutDirtyChecking(), []);

  const handleDownloadPDF = useCallback(() => {
    api.getCaseReportPdfUrl(report.id, (result: any) => {
      if (result.uploaded && hasStringValue(result.signedURL)) openPage(result.signedURL);
      else App.showPopupNotification('Error downloading PDF. Try again in 15 seconds.', 'error');
    }, () => {
      App.showPopupNotification('Error downloading PDF.', 'error');
    });
  }, [report?.id]);

  // ── Render ────────────────────────────────────────────────────────

  if (!isInitialized || !report) {
    return <div className={`case-report-form-screen${theme ? ' ' + theme : ''}`} />;
  }

  return (
    <div className={`case-report-form-screen${theme ? ' ' + theme : ''}`}>
      {/* Tab bar */}
      <nav className="case-report-sub-navigation">
        <div className="case-report-tabs-container" ref={tabsContainerRef}>
          <ul className="case-report-tabs-list" role="tablist" ref={tabsListRef}>
            {allTabIds.map(tabId => (
              <li
                key={tabId}
                role="presentation"
                data-tab-id={tabId}
                className={`case-report-tab${activeTab === tabId ? ' active' : ''}${!visibleTabIds.includes(tabId) ? ' hidden-tab' : ''}`}
                style={!visibleTabIds.includes(tabId) ? { position: 'absolute', visibility: 'hidden', pointerEvents: 'none' } : undefined}
              >
                <a href={`#${tabId}`} onClick={(e) => { e.preventDefault(); selectTab(tabId); }}>
                  {getTabLabel(tabId)}
                  {getTabCount(tabId) != null && (
                    <span className="badge">{getTabCount(tabId)}</span>
                  )}
                </a>
              </li>
            ))}
          </ul>
          {overflowTabIds.length > 0 && (
            <div className="case-report-tabs-more" ref={tabsMoreRef}>
              <button type="button" className="case-report-tabs-more-btn" onClick={() => setShowMoreMenu(m => !m)}>
                More <i className="fa fa-chevron-down"></i>
              </button>
              {showMoreMenu && (
                <ul className="case-report-tabs-more-menu" role="menu">
                  {overflowTabIds.map(tabId => (
                    <li key={tabId}>
                      <a href={`#${tabId}`} onClick={(e) => { e.preventDefault(); selectTab(tabId); }}>
                        {getTabLabel(tabId)}
                        {getTabCount(tabId) != null && <span className="badge">{getTabCount(tabId)}</span>}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Case Report Header */}
      <div className="case-report-header">
        <div className="case-report-header-content">
          <h1 className="case-report-title">Case Report</h1>
          <span className="case-report-id-badge">{report.caseId || 'New'}</span>
        </div>
        <div className="case-report-actions">
          {!isReadOnly && (
            <button type="button" className="btn-header btn-header-save" onClick={handleSave} disabled={isSaving}>
              <i className="fa fa-save"></i> {saveButtonText}
            </button>
          )}
          {report.isApproved && (
            <button type="button" className="btn-header btn-header-primary" onClick={handleDownloadPDF}>
              <i className="fa fa-download"></i> PDF
            </button>
          )}
          <button type="button" className="btn-header btn-header-exit" onClick={isReadOnly ? handleExit : handleExitWithoutSave}>
            <i className="fa fa-sign-out"></i> Exit
          </button>
        </div>
      </div>

      {/* Dispatcher Notes (read-only) */}
      {dispatcherNotes && (
        <div className="case-report-disposition-notes">
          <label className="field-label">Call Disposition Notes</label>
          <textarea rows={2} className="form-textarea" value={dispatcherNotes} disabled />
        </div>
      )}

      {/* Tab content */}
      <div className="case-report-tab-content">

        {/* ── Incident Tab ──────────────────────────────────────── */}
        {activeTab === 'Incident' && (
          <div className="tab-pane case-report-details-tab" role="tabpanel" id="Incident">
            <div className="details-grid">
              <div className="details-column-left">
                <CaseReportIncidentDetails
                  report={report}
                  isReadOnly={isReadOnly}
                  natureOfCallOptions={natureOfCallOptions}
                  natureOfCallGroupBy={listHasOtherCompanyItems(natureOfCallOptions) ? 'companyName' : null}
                  onNatureChange={handleNatureChange}
                  onFieldChange={handleFieldChange}
                />
              </div>
              <div className="details-column-right">
                <CaseReportLocation
                  report={report}
                  isReadOnly={isReadOnly}
                  onReportChange={handleLocationChange}
                  onViewMap={handleViewMap}
                  onLocationBlur={handleLocationBlur}
                />
              </div>
            </div>
            <CaseReportOfficersAssigned
              officers={officers}
              people={availableOfficers}
              title={`Responding ${officerPluralLabel}`}
              isReadOnly={isReadOnly}
              onChange={handleOfficersChange}
            />
          </div>
        )}

        {/* ── Involved Tab ─────────────────────────────────────── */}
        {activeTab === 'Involved' && (
          <CaseReportInvolvedSection
            ref={involvedSectionRef}
            people={people}
            vehicles={vehicles}
            businesses={businesses}
            clients={clients}
            isReadOnly={isReadOnly}
            canSaveNpsdData={canSaveNpsdData}
            onNpsdChange={refreshNpsdFromReport}
          />
        )}

        {/* ── Agencies Tab ─────────────────────────────────────── */}
        {activeTab === 'Agencies' && (
          <div className="tab-panel">
            <CaseReportRespondingAgencies
              agencies={agencies}
              onChange={handleAgenciesChange}
            />
          </div>
        )}

        {/* ── Offense Tab ──────────────────────────────────────── */}
        {activeTab === 'Offense' && (
          <CaseReportOffenseSection
            offenders={offenders}
            setOffenders={setOffenders}
            involvedPersonOptions={involvedPersonOptions}
            offenseOptions={offenseOptions}
            isReadOnly={isReadOnly}
            officerLabel={officerLabel}
          />
        )}

        {/* ── Accident Tab ─────────────────────────────────────── */}
        {activeTab === 'Accident' && (
          <CaseReportAccidentSection
            accident={accident}
            setAccident={setAccident}
            involvedPersonOptions={involvedPersonOptions}
            involvedVehicleOptions={involvedVehicleOptions}
            isReadOnly={isReadOnly}
            officerLabel={officerLabel}
          />
        )}

        {/* ── Property Tab ─────────────────────────────────────── */}
        {activeTab === 'Property' && (
          <CaseReportPropertySection
            propertyList={propertyList}
            setPropertyList={setPropertyList}
            propertyOwnerOptions={propertyOwnerOptions}
            reportedAsOptions={reportedAsOptions}
            isReadOnly={isReadOnly}
            officerLabel={officerLabel}
          />
        )}

        {/* ── Medical Tab ──────────────────────────────────────── */}
        {activeTab === 'Medical' && (
          <CaseReportMedicalSection
            medicalReports={medicalReports}
            setMedicalReports={setMedicalReports}
            involvedPersonOptions={involvedPersonOptions}
            isReadOnly={isReadOnly}
          />
        )}

        {/* ── Evidence Tab ─────────────────────────────────────── */}
        {activeTab === 'Evidence' && (
          <CaseReportEvidenceSection
            statementList={statementList}
            setStatementList={setStatementList}
            digitalEvidenceList={digitalEvidenceList}
            setDigitalEvidenceList={setDigitalEvidenceList}
            involvedPersonOptions={involvedPersonOptions}
            involvedVehicleOptions={involvedVehicleOptions}
            businesses={businesses}
            clients={clients}
            evidenceOfOptions={evidenceOfOptions}
            allInvolvedOptions={allInvolvedOptions}
            isReadOnly={isReadOnly}
          />
        )}

        {/* ── Narrative Tab ────────────────────────────────────── */}
        {activeTab === 'Narrative' && (
          <div className="tab-pane" role="tabpanel" id="Narrative">
            <div className={`case-report-details-panel ${theme}`}>
              <div className="panel-header">Narrative</div>
              <div className="panel-body">
                <RichTextEditor
                  ref={narrativeEditorRef}
                  value={narrativeContent}
                  isReadOnly={isReadOnly}
                  height="400px"
                  onImageUpload={handleNarrativeImageUpload}
                  toolbarConfig={narrativeToolbarConfig}
                  placeholder=""
                  onChange={(html: string) => {
                    setNarrativeContent(html);
                    CaseRpt.currentMDTRpt.narrative = html;
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Review / Submit Tab ──────────────────────────────── */}
        {activeTab === 'ReviewSubmit' && (
          <CaseReportReviewSection
            report={report}
            hasReportApproval={hasApprovalPerm}
            isAuthor={isAuthor}
            canUnapprove={canUnapprove}
            onSubmitForApproval={handleSubmitForApproval}
            onApprove={handleApprove}
            onUnapprove={handleUnapprove}
            onReturnToAuthor={handleReturnToAuthor}
          />
        )}

        {/* ── Custom Form Tabs ─────────────────────────────────────
            One tab per CASE_REPORT-attached schema. Panels stay mounted and
            are hidden with display:none so edits in one form survive switching
            tabs before the report is saved. Editable for Form Entry holders
            while the report is open; approved reports are locked server-side
            (form.instance.host.locked), so the panels go read-only with it. */}
        {(formSchemas || []).map((schema) => {
          const tabId = `Form_${schema.id}`;
          return (
            <div
              key={tabId}
              className="tab-pane"
              role="tabpanel"
              id={tabId}
              style={{ display: activeTab === tabId ? undefined : 'none' }}
            >
              <FormTabPanel
                schemaId={schema.id}
                recordType="CASE_REPORT"
                recordId={report?.id ? Number(report.id) : null}
                hostNoun="report"
                isEditMode={formsEditable}
                saveTrigger={formSaveTrigger}
                onValidationChange={handleFormValidation}
                onDirtyChange={handleFormDirtyChange}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Imperative Mount (called from app.ts router)
// ============================================================================

function displayCaseReport(id: any, isLog: any, viewOnly: boolean = false, currentTab: string): void {
  const container = document.getElementById('CaseReportContainer');
  if (!container) return;
  renderReactComponent(CaseReportFormScreen, { reportId: id, isLog: isLog, viewOnly: viewOnly, currentTab: currentTab }, container);
}

// ============================================================================
// Window Registration
// ============================================================================

(window as any).CaseReportFormScreenReact = CaseReportFormScreen;
(window as any).displayCaseReport = displayCaseReport;
